import { useState, useRef, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';

interface ModalOverrideBaseProps {
  /** Valor auto-sugerido = base atual da venda (cartao cheio). Vira referencia no log. */
  autoSugerido: number;
  onAplicar: (valor: number) => void;
  onCancel: () => void;
}

const fmt = (v: number): string =>
  Number(v).toLocaleString('pt-BR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

/**
 * Override manual da base da venda (atalho Ctrl+Shift+X) — uso RARO, so admin/gerente.
 * O valor informado vira a base da venda (segue pra venda + nota + parcelamento); a comissao
 * deriva sozinha desse valor menos o acrescimo do cartao (DA-169). Casos de venda com problema
 * (ex.: combinada ontem, reajustada hoje). Ver project_override_base_parcelamento.
 */
export function ModalOverrideBase({ autoSugerido, onAplicar, onCancel }: ModalOverrideBaseProps) {
  const [valorInput, setValorInput] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const t = setTimeout(() => inputRef.current?.focus(), 50);
    return () => clearTimeout(t);
  }, []);

  function aplicar() {
    const val = parseFloat(String(valorInput).replace(/\./g, '').replace(',', '.')) || 0;
    if (val <= 0) return;
    onAplicar(Math.round(val * 100) / 100);
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="modal-conteudo bg-white rounded-2xl shadow-2xl p-6 w-full max-w-md" style={{ position: 'relative' }}>
        <button type="button" className="btn-fechar-modal btn-fechar-modal--abs" onClick={onCancel} aria-label="Fechar">×</button>

        <div className="modal-header flex items-center gap-3 mb-3 cursor-move">
          <div className="p-2 rounded-lg" style={{ backgroundColor: '#fde68a' }}>
            <AlertTriangle size={20} style={{ color: '#000' }} />
          </div>
          <div>
            <h3 className="font-semibold text-gray-800">Base manual da venda</h3>
            <p className="text-sm text-gray-500">Uso restrito — venda com problema</p>
          </div>
        </div>

        <p className="text-sm text-gray-600 mb-4 leading-relaxed">
          O valor informado vira a <strong>base da venda</strong> — segue para a venda, a nota e o
          parcelamento. A comissao e calculada sobre esse valor (menos o acrescimo do cartao).
          Fica registrado quem aplicou.
        </p>

        <div className="mb-4 rounded-lg px-3 py-2 text-sm" style={{ backgroundColor: '#f3f4f6' }}>
          <span className="text-gray-500">Valor atual (sugerido): </span>
          <span className="font-semibold tabular-nums text-gray-800">R$ {fmt(autoSugerido)}</span>
        </div>

        <label className="block text-sm font-medium text-gray-700 mb-1">Novo valor da venda</label>
        <div className="flex items-center gap-2">
          <span className="text-gray-500 font-semibold">R$</span>
          <input
            ref={inputRef}
            type="text"
            inputMode="decimal"
            value={valorInput}
            onChange={(e) => setValorInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') aplicar();
              if (e.key === 'Escape') onCancel();
            }}
            placeholder={fmt(autoSugerido)}
            className="flex-1 px-3 py-2.5 border border-gray-300 rounded-lg text-lg tabular-nums focus:ring-2 focus:ring-blue-500 outline-none"
          />
        </div>

        <div className="flex gap-2 mt-5">
          <button
            type="button"
            onClick={onCancel}
            className="flex-1 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-200 transition"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={aplicar}
            disabled={!valorInput || (parseFloat(String(valorInput).replace(/\./g, '').replace(',', '.')) || 0) <= 0}
            className="flex-1 px-4 py-2 bg-blue-600 text-white rounded-lg text-sm font-medium hover:bg-blue-700 transition disabled:opacity-50"
          >
            Aplicar a venda
          </button>
        </div>
      </div>
    </div>
  );
}
